import { forwardRef, type AnchorHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';

type SmartLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
};

/**
 * Renders a React Router <Link> for internal paths so navigation stays
 * client-side (preserving Lenis state and triggering View Transitions).
 *
 * Falls back to a plain <a> for:
 *   - external URLs (http://, https://, //)
 *   - mailto:, tel:, sms:
 *   - in-page anchors (#…)
 */
const SmartLink = forwardRef<HTMLAnchorElement, SmartLinkProps>(function SmartLink(
  { href, children, ...rest },
  ref,
) {
  const isExternal =
    /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(href) || href.startsWith('#');

  if (isExternal) {
    return (
      <a ref={ref} href={href} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <Link ref={ref} to={href} viewTransition {...rest}>
      {children}
    </Link>
  );
});

export default SmartLink;
